import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function DeploymentPage(): Node {
  return html`
    <div>
      <h1>Deployment</h1>
      <p>Microfrontends in onefold are independently deployable ES modules. Each team owns their remote, deploys on their own schedule, and the host loads them at runtime.</p>

      <h2>Independent Deployment</h2>
      <ul>
        <li>Each remote is built and deployed separately.</li>
        <li>The host only knows the remote's URL — not its source.</li>
        <li>Teams can use different CI/CD pipelines, release cadences, and environments.</li>
        <li>SRI hashes are updated in the host config when a remote deploys.</li>
      </ul>

      <h2>Project Structure</h2>
      ${CodeBlock(`my-microfrontend-app/
├── host/                    # Host shell application
│   ├── src/
│   │   ├── main.ts         # Router + loadRemote calls
│   │   └── config.ts       # Remote URLs + integrity hashes
│   ├── package.json
│   └── build.mjs
├── remotes/
│   ├── billing/            # Billing team's widget
│   │   ├── src/index.ts
│   │   ├── package.json
│   │   └── build.mjs
│   ├── analytics/          # Analytics team's widget
│   │   ├── src/index.ts
│   │   ├── package.json
│   │   └── build.mjs
│   └── shared/             # Shared component library
└── package.json            # Workspace root (optional)`)}

      <h2>Performance Features</h2>
      <table>
        <tr><th>Feature</th><th>Description</th></tr>
        <tr><td>Lazy loading</td><td>Remotes load only when their route is visited.</td></tr>
        <tr><td>Parallel loading</td><td>Multiple remotes can load concurrently.</td></tr>
        <tr><td>Caching</td><td>Loaded modules are cached — subsequent navigations are instant.</td></tr>
        <tr><td>Prefetch</td><td>Preload remotes on hover/idle for perceived performance.</td></tr>
        <tr><td>Fallback UI</td><td>Show skeleton/spinner while loading.</td></tr>
      </table>

      <h2>Error Handling</h2>
      ${CodeBlock(`loadRemote({
  url: remoteConfig.billingUrl,
  integrity: remoteConfig.billingHash,
  timeout: 5000,
  fallback: () => html\`<div class="skeleton">Loading billing...</div>\`,
  onError: (err) => html\`
    <div class="error-card">
      <h3>Billing widget unavailable</h3>
      <p>\${err.message}</p>
      <button onclick=\${() => location.reload()}>Retry</button>
    </div>
  \`,
});`)}

      <h2>CLI Scaffold</h2>
      <p>Use <code>create-onefold</code> to scaffold a microfrontend project:</p>
      ${CodeBlock(`npm create onefold@latest my-app -- --template microfrontend

# Creates:
# my-app/
#   host/          — Host shell with router
#   remotes/       — Example remote widgets
#   build.mjs      — Build script for all packages`)}

      ${Callout('Each remote should be served with immutable cache headers (e.g., Cache-Control: public, max-age=31536000, immutable) and content-addressed filenames for cache busting.')}
    </div>
  `;
}
